import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { axiosInstance, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, LogOut, Download, Plus, History, Zap, Clock, Settings, ExternalLink } from "lucide-react";

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [designSettings, setDesignSettings] = useState({
    logo_url: null,
    background_color: "#7C3AED",
    button_color: "#3B82F6"
  });
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    order_id: "",
    delivery_note: "",
    service_type: "standard",
  });
  
  const [packageCount, setPackageCount] = useState(1);
  const [packages, setPackages] = useState([
    { weight: "", length: "", width: "", height: "" }
  ]);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showFileUpload, setShowFileUpload] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      // Check if receiver address is Switzerland
      checkIfSwitzerlandShipment(parsedUser.receiver_address_id);
    }
    fetchLabels();
    loadDesignSettings();
  }, []);

  const checkIfSwitzerlandShipment = async (receiverAddressId) => {
    try {
      if (!receiverAddressId) {
        console.log("No receiver address ID provided");
        return;
      }
      console.log("Checking address:", receiverAddressId);
      const response = await axiosInstance.get(`/addresses/${receiverAddressId}`);
      console.log("Address data:", response.data);
      if (response.data && response.data.country === 'CH') {
        console.log("✅ Switzerland shipment detected - showing file upload");
        setShowFileUpload(true);
      } else {
        console.log("Not a Switzerland shipment, country:", response.data?.country);
        setShowFileUpload(false);
      }
    } catch (error) {
      console.error("Error checking address:", error);
    }
  };

  const loadDesignSettings = async () => {
    try {
      const response = await axiosInstance.get("/design-settings");
      setDesignSettings(response.data);
    } catch (error) {
      console.error("Error loading design settings:", error);
    }
  };

  const fetchLabels = async () => {
    try {
      const response = await axiosInstance.get("/shipping/labels");
      setLabels(response.data);
    } catch (error) {
      console.error("Error fetching labels:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const goToAdmin = () => {
    navigate("/admin");
  };

  const handlePackageCountChange = (count) => {
    const newCount = Math.min(Math.max(1, parseInt(count) || 1), 20);
    setPackageCount(newCount);
    
    // Adjust packages array
    const currentPackages = [...packages];
    if (newCount > currentPackages.length) {
      // Add new packages
      for (let i = currentPackages.length; i < newCount; i++) {
        currentPackages.push({ weight: "", length: "", width: "", height: "" });
      }
    } else if (newCount < currentPackages.length) {
      // Remove excess packages
      currentPackages.splice(newCount);
    }
    setPackages(currentPackages);
  };

  const handlePackageChange = (index, field, value) => {
    const newPackages = [...packages];
    newPackages[index][field] = value;
    setPackages(newPackages);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least one of order_id or delivery_note is filled
    if (!formData.order_id && !formData.delivery_note) {
      toast.error(t('label.atLeastOneFieldRequired'));
      return;
    }
    
    // Validate packages
    for (let i = 0; i < packages.length; i++) {
      const pkg = packages[i];
      if (!pkg.weight || !pkg.length || !pkg.width || !pkg.height) {
        toast.error(`${t('label.fillDetails')} ${t('label.package')} ${i + 1}`);
        return;
      }
    }
    
    setLoading(true);

    try {
      // Prepare request payload
      const requestPayload = {
        order_id: formData.order_id,
        delivery_note: formData.delivery_note,
        service_type: formData.service_type,
      };
      
      // Add packages data
      if (showFileUpload && packageCount > 1) {
        // Multi-package for Switzerland
        requestPayload.packages = packages.map(pkg => ({
          weight: parseFloat(pkg.weight),
          length: parseFloat(pkg.length),
          width: parseFloat(pkg.width),
          height: parseFloat(pkg.height),
        }));
      } else {
        // Single package (backwards compatibility)
        requestPayload.weight = parseFloat(packages[0].weight);
        requestPayload.length = parseFloat(packages[0].length);
        requestPayload.width = parseFloat(packages[0].width);
        requestPayload.height = parseFloat(packages[0].height);
      }
      
      const response = await axiosInstance.post("/shipping/create-label", requestPayload);

      const newLabel = response.data;
      
      // Upload documents if this is a Switzerland shipment and files are selected
      if (showFileUpload && uploadedFiles.length > 0) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append("tracking_number", newLabel.tracking_number);
          uploadedFiles.forEach((file) => {
            formDataUpload.append("files", file);
          });
          
          const uploadResponse = await axiosInstance.post("/shipping/upload-documents", formDataUpload, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          
          console.log("Upload response:", uploadResponse.data);
          
          // Show appropriate message based on UPS upload status
          if (uploadResponse.data.ups_upload_success) {
            toast.success(t('label.created') + " - ✅ " + uploadResponse.data.message);
          } else if (uploadResponse.data.ups_status === "partial") {
            toast.warning(t('label.created') + " - ⚠️ " + uploadResponse.data.message);
          } else {
            toast.error(t('label.created') + " - ❌ " + uploadResponse.data.message);
          }
        } catch (uploadError) {
          console.error("Document upload error:", uploadError);
          toast.warning(t('label.created') + " - Dokument-Upload fehlgeschlagen");
        }
      } else {
        toast.success(t('label.created'));
      }
      
      // Reset form
      setFormData({
        order_id: "",
        delivery_note: "",
        service_type: "standard",
      });
      setPackages([{ weight: "", length: "", width: "", height: "" }]);
      setPackageCount(1);
      setUploadedFiles([]);
      
      // Refresh labels list
      fetchLabels();
      
      // Automatically open the PDF for the newly created label
      if (newLabel && newLabel.id && newLabel.tracking_number) {
        setTimeout(() => {
          handleDownloadPDF(newLabel.id, newLabel.tracking_number);
        }, 500); // Small delay to ensure label is saved
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Labels");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (labelId, trackingNumber) => {
    try {
      // Use fetch with no-cors clone workaround to avoid recorder interception
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/shipping/label/${labelId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        toast.error(`${t('label.pdfError')}: ${response.status}`);
        return;
      }

      // Get blob directly without cloning to avoid recorder issues
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and click it to download
      const link = document.createElement("a");
      link.href = url;
      link.download = `label_${trackingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Also try to open in new window
      setTimeout(() => {
        window.open(url, "_blank");
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }, 100);
      
      toast.success(t('label.pdfDownloaded'));
    } catch (error) {
      console.error("PDF Download Error:", error);
      toast.error(t('label.pdfError'));
    }
  };

  const getUPSTrackingUrl = (trackingNumber) => {
    const localeMap = {
      'de': 'de_DE',
      'en': 'en_US',
      'cs': 'cs_CZ',
      'ro': 'ro_RO'
    };
    const locale = localeMap[i18n.language] || 'en_US';
    return `https://www.ups.com/track?loc=${locale}&tracknum=${trackingNumber}`;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="dashboard-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-[500px] h-[500px] rounded-full blur-3xl" style={{ backgroundColor: `${designSettings.background_color}08` }}></div>
        <div className="absolute -bottom-1/4 -right-1/4 w-[500px] h-[500px] rounded-full blur-3xl" style={{ backgroundColor: `${designSettings.button_color}08` }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 p-6 rounded-2xl shadow-lg" style={{ background: `linear-gradient(135deg, ${designSettings.background_color}, ${designSettings.button_color})` }}>
          <div className="flex items-center gap-4">
            {designSettings.logo_url ? (
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                <img src={designSettings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shadow-lg">
                <Package className="w-7 h-7 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('dashboard.welcome')}, {user?.company_name}
              </h1>
              <p className="text-white/80 text-sm mt-1">{t('dashboard.createLabels')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {user?.role === "admin" && (
              <Button
                onClick={goToAdmin}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
                data-testid="admin-button"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('dashboard.admin')}
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 backdrop-blur-sm"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="create" className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1 shadow-sm">
            <TabsTrigger
              value="create"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="create-label-tab"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.newLabel')}
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-gray-400 data-[state=active]:text-white text-gray-700"
              data-testid="history-tab"
            >
              <History className="w-4 h-4 mr-2" />
              {t('dashboard.history')}
            </TabsTrigger>
          </TabsList>

          {/* Create Label Tab */}
          <TabsContent value="create">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl">{t('label.createNew')}</CardTitle>
                <CardDescription className="text-base">
                  {t('label.fillDetails')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Service Type */}
                  <div className="space-y-2">
                    <Label htmlFor="service_type" className="text-sm font-medium">
                      {t('label.serviceType')}
                    </Label>
                    <Select
                      value={formData.service_type}
                      onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="service-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard" data-testid="service-standard">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {t('label.standard')}
                          </div>
                        </SelectItem>
                        <SelectItem value="express" data-testid="service-express">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            {t('label.expressSaver')}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Package Count - Only for Switzerland */}
                  {showFileUpload && (
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-5 space-y-3 shadow-md">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-md">
                          <span className="text-white text-lg font-bold">📦</span>
                        </div>
                        <div>
                          <Label htmlFor="packageCount" className="text-base font-bold text-indigo-900">
                            {t('label.numberOfPackages')}
                          </Label>
                          <p className="text-xs text-indigo-600">{t('label.maxPackagesPerShipment')}</p>
                        </div>
                      </div>
                      <Input
                        id="packageCount"
                        type="number"
                        min="1"
                        max="20"
                        value={packageCount}
                        onChange={(e) => handlePackageCountChange(e.target.value)}
                        className="h-12 bg-white border-indigo-300 focus:border-indigo-500 focus:ring-indigo-500 shadow-sm text-lg font-semibold"
                      />
                      <div className="flex items-start gap-2 bg-white/70 rounded-lg p-3">
                        <span className="text-indigo-500">ℹ️</span>
                        <p className="text-xs text-indigo-700 leading-relaxed">
                          {t('label.switzerlandMultiPackageInfo')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Package Details */}
                  {packages.map((pkg, index) => (
                    <div key={index} className="relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br from-blue-50 to-purple-50 p-6 space-y-5">
                      {/* Header with gradient */}
                      <div className="flex items-center justify-between pb-3 border-b border-blue-200/50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-md">
                            <Package className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {showFileUpload && packageCount > 1 
                              ? `${t('label.package')} ${index + 1} von ${packageCount}`
                              : t('label.package')
                            }
                          </h3>
                        </div>
                      </div>
                      
                      {/* Weight */}
                      <div className="space-y-2">
                        <Label htmlFor={`weight-${index}`} className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <span className="text-blue-500">⚖️</span>
                          {t('label.weight')} ({t('label.weightUnit')})
                        </Label>
                        <Input
                          id={`weight-${index}`}
                          type="number"
                          step="0.1"
                          placeholder="z.B. 2.5"
                          value={pkg.weight}
                          onChange={(e) => handlePackageChange(index, 'weight', e.target.value)}
                          required
                          className="h-12 bg-white border-blue-200 focus:border-blue-400 focus:ring-blue-400 shadow-sm"
                        />
                      </div>

                      {/* Dimensions */}
                      <div>
                        <Label className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="text-purple-500">📐</span>
                          {t('label.dimensions')} ({t('label.dimensionsUnit')})
                        </Label>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor={`length-${index}`} className="text-xs font-medium text-gray-600">
                              {t('label.length')}
                            </Label>
                            <Input
                              id={`length-${index}`}
                              type="number"
                              step="0.1"
                              placeholder="L"
                              value={pkg.length}
                              onChange={(e) => handlePackageChange(index, 'length', e.target.value)}
                              required
                              className="h-12 bg-white border-blue-200 focus:border-purple-400 focus:ring-purple-400 shadow-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`width-${index}`} className="text-xs font-medium text-gray-600">
                              {t('label.width')}
                            </Label>
                            <Input
                              id={`width-${index}`}
                              type="number"
                              step="0.1"
                              placeholder="B"
                              value={pkg.width}
                              onChange={(e) => handlePackageChange(index, 'width', e.target.value)}
                              required
                              className="h-12 bg-white border-blue-200 focus:border-purple-400 focus:ring-purple-400 shadow-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`height-${index}`} className="text-xs font-medium text-gray-600">
                              {t('label.height')}
                            </Label>
                            <Input
                              id={`height-${index}`}
                              type="number"
                              step="0.1"
                              placeholder="H"
                              value={pkg.height}
                              onChange={(e) => handlePackageChange(index, 'height', e.target.value)}
                              required
                              className="h-12 bg-white border-blue-200 focus:border-purple-400 focus:ring-purple-400 shadow-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Order Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="order_id" className="text-sm font-medium">
                        {t('label.orderId')} <span className="text-gray-400 text-xs">{t('label.optional')}</span>
                      </Label>
                      <Input
                        id="order_id"
                        type="text"
                        placeholder="z.B. ORD-12345"
                        value={formData.order_id}
                        onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                        className="h-11"
                        data-testid="order-id-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="delivery_note" className="text-sm font-medium">
                        {t('label.deliveryNote')} <span className="text-gray-400 text-xs">{t('label.optional')}</span>
                      </Label>
                      <Input
                        id="delivery_note"
                        type="text"
                        placeholder="z.B. DN-67890"
                        value={formData.delivery_note}
                        onChange={(e) => setFormData({ ...formData, delivery_note: e.target.value })}
                        className="h-11"
                        data-testid="delivery-note-input"
                      />
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 -mt-2">
                    <span className="text-amber-600">ℹ️</span> {t('label.atLeastOneFieldRequired')}
                  </p>

                  {/* File Upload for Switzerland Shipments */}
                  {showFileUpload && (
                    <div className="space-y-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="font-medium text-yellow-800">{t('label.switzerlandDocumentsRequired')}</p>
                          <p className="text-sm text-yellow-700 mt-1">
                            {t('label.switzerlandDocumentsUploadInfo')}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="documents" className="text-sm font-medium text-yellow-800">
                          {t('label.uploadDocumentsPdf')}
                        </Label>
                        <Input
                          id="documents"
                          type="file"
                          accept=".pdf"
                          multiple
                          onChange={handleFileChange}
                          className="h-11 bg-white cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-yellow-100 file:text-yellow-700 hover:file:bg-yellow-200"
                        />
                        <p className="text-xs text-yellow-600 italic">
                          💡 {t('label.multiFileSelectionTip')}
                        </p>
                        {uploadedFiles.length > 0 && (
                          <div className="text-sm text-yellow-700 mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                            <strong>{uploadedFiles.length} {t('label.filesSelected')}</strong>
                            <ul className="list-disc list-inside mt-1">
                              {uploadedFiles.map((f, idx) => (
                                <li key={idx}>{f.name}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 text-white font-medium text-base shadow-lg hover:shadow-xl transition-all duration-200 hover:opacity-90"
                    style={{ background: `linear-gradient(to right, ${designSettings.background_color}, ${designSettings.button_color})` }}
                    data-testid="create-label-button"
                  >
                    {loading ? t('label.creating') : t('label.createButton')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl">{t('label.allLabels')}</CardTitle>
                <CardDescription className="text-base">
                  {t('label.allLabels')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {labels.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-labels-message">
                    <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg">{t('label.noLabels')}</p>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="labels-list">
                    {labels.map((label) => (
                      <div
                        key={label.id}
                        className="border border-gray-200 rounded-lg p-5 hover:border-blue-300 transition-colors bg-white"
                        data-testid={`label-item-${label.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-3 flex-1">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                {label.service_type === "express" ? (
                                  <><Zap className="w-3 h-3 mr-1" /> {t('label.expressSaver')}</>
                                ) : (
                                  <><Clock className="w-3 h-3 mr-1" /> {t('label.standard')}</>
                                )}
                              </span>
                              <a
                                href={getUPSTrackingUrl(label.tracking_number)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-mono text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                              >
                                {label.tracking_number}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-gray-500">{t('label.weight')}</p>
                                <p className="font-medium">{label.weight} {t('label.weightUnit')}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">{t('label.dimensions')}</p>
                                <p className="font-medium">
                                  {label.length}×{label.width}×{label.height} {t('label.dimensionsUnit')}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">{t('label.orderId')}</p>
                                <p className="font-medium">{label.order_id}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">{t('label.deliveryNote')}</p>
                                <p className="font-medium">{label.delivery_note}</p>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400">
                              {t('label.createdAt')}: {formatDate(label.created_at)}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleDownloadPDF(label.id, label.tracking_number)}
                            variant="outline"
                            className="ml-4 flex-shrink-0"
                            data-testid={`download-pdf-${label.id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('label.downloadPdf')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Footer */}
      <footer className="mt-12 py-6 text-center text-sm text-gray-600 border-t">
        <div className="space-y-2">
          <p>© 2025 - Kauz Logistik e.K. - Industriestr. 18 - DE-63834 Sulzbach</p>
          <p><a href="mailto:mbe0020@mbe.de" className="text-blue-600 hover:underline">mbe0020@mbe.de</a></p>
        </div>
      </footer>
    </div>
  );
}
